import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mailbox, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/cd8e3a71-ec3e-4a39-bf67-778ba7cb11b8/images/a9c5042425f93cafea56ddac062e2f602ee99631882750de86a39ef5120547cc.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [twofa, setTwofa] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password, twofa ? code : undefined);
      if (res.twofaRequired) {
        setTwofa(true);
        toast.info("Enter your 6-digit authenticator code");
      } else {
        toast.success("Welcome back");
        navigate("/");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-white">
              <Mailbox className="h-5 w-5" />
            </div>
            <div>
              <p className="font-heading text-xl font-bold tracking-tighter leading-none">MailShift</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Migration Engine</p>
            </div>
          </div>

          <h1 className="font-heading text-4xl font-bold tracking-tighter sm:text-5xl">Sign in</h1>
          <p className="mt-3 text-muted-foreground">Access your IMAP migration dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em]">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em]">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     required data-testid="login-password-input" />
            </div>
            {twofa && (
              <div className="space-y-2">
                <Label htmlFor="code" className="text-xs uppercase tracking-[0.15em]">Authenticator code</Label>
                <Input id="code" inputMode="numeric" autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                       placeholder="123456" maxLength={6} className="font-mono tracking-[0.4em]"
                       data-testid="login-2fa-input" />
                <p className="text-xs text-muted-foreground">Open your authenticator app and enter the 6-digit code.</p>
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full gap-2" data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              {twofa ? "Verify & sign in" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            No account?{" "}
            <Link to="/register" className="font-medium text-primary hover:underline" data-testid="go-register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img src={HERO} alt="Data transfer" className="h-screen w-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-secondary/60" />
        <div className="absolute bottom-0 p-12">
          <p className="font-heading text-3xl font-bold leading-tight tracking-tight text-white">
            Migrate mailboxes directly, provider to provider.
          </p>
          <p className="mt-3 max-w-md text-white/70">
            Bulk IMAP migrations with real-time progress, folder mapping and live logs.
          </p>
        </div>
      </div>
    </div>
  );
}
